import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import toUpper from 'lodash/toUpper';
import { useSearchParams } from 'react-router-dom';
import {
  ENTITY_ID_SEARCH_PARAM_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';
import { CURRENT_USER_REGISTERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/registers.tsx';
import { CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/transactions.tsx';
import {
  GetEntityShareholdersResponse,
  GetUserRegistersResponse,
  RegisterType,
  ShareholderExtendResponse,
  UserRegisterResponse,
} from 'api';

interface Strings {
  application: string;
  dividend: string;
  nav: string;
  share: string;
  [p: string]: string;
}

export const registerTypeStrings: Record<RegisterType, Strings> = {
  [RegisterType.Company]: {
    application: 'application',
    dividend: 'dividend',
    nav: 'Price',
    share: 'share',
  },
  [RegisterType.Fund]: {
    application: 'subscription',
    dividend: 'distribution',
    nav: 'NAV',
    share: 'unit',
  },
};

export const useRegisterTypeStrings = () => {
  const [params] = useSearchParams();
  const registersListIssuer = get(
    queryClient.getQueryData<GetUserRegistersResponse>([CURRENT_USER_REGISTERS_QUERY_KEY]),
    'data',
    [],
  );
  const currentEntityId = params.get(ENTITY_ID_SEARCH_PARAM_KEY);
  const registersListInvestor = get(
    queryClient.getQueryData<GetEntityShareholdersResponse>([
      CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY,
      +(currentEntityId || 0),
    ]),
    'shareholders',
    [],
  );

  const registersList = (
    registersListInvestor?.length ? registersListInvestor : registersListIssuer
  ) as (ShareholderExtendResponse & UserRegisterResponse)[];

  return (
    key: keyof Strings | string,
    options?: {
      plural?: boolean;
      capitalize?: boolean;
      toUpper?: boolean;
      toLower?: boolean;
      registerId?: string | number;
    },
  ) => {
    const defaultValue = key as string;
    const firstInvestorRegisterId = registersList?.[0]?.register_id;
    // if (options?.capitalize) defaultValue = capitalize(defaultValue);
    // if (options?.plural) defaultValue += 's';
    // if (options?.toLower) defaultValue = toLower(defaultValue);
    // if (options?.toUpper) defaultValue = toUpper(defaultValue);

    const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);

    let currentRegisterId = options?.registerId || registerId || 0;
    currentRegisterId = currentRegisterId === 'all' ? firstInvestorRegisterId : currentRegisterId;

    const selectedRegister = registersList.find(
      item => get(item, 'register_obj.id', 0) === +currentRegisterId,
    );

    const selectedRegisterType = get(selectedRegister, 'register_obj.type', null);

    if (!selectedRegisterType || !Object.values(RegisterType).includes(selectedRegisterType)) {
      return defaultValue;
    }

    if (
      !registersList ||
      !Array.isArray(registersList) ||
      !registersList.length ||
      isNaN(+currentRegisterId)
    ) {
      return defaultValue;
    }

    let res =
      registerTypeStrings[selectedRegisterType as RegisterType]?.[
        String(key).toLowerCase() as keyof Strings
      ];

    if (options?.capitalize) res = capitalize(res);
    if (options?.plural) res += 's';
    if (options?.toLower) res = toLower(res);
    if (options?.toUpper) res = toUpper(res);

    return res;
  };
};
